import React, { FC, ReactElement, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission } from '../contexts/permissionContext';
import AuthContext from '../contexts/authContext';
import { authPagesMenu } from '../menu';

interface ProtectedRouteProps {
  /** 路由权限 key（对应 ROUTER_KEYS） */
  routerKey?: string;
  /** 子元素 */
  children: ReactElement;
  /** 无权限时重定向路径（默认 /auth-pages/404） */
  redirectPath?: string;
}

/**
 * 受保护的路由组件
 * 检查用户是否有访问该路由的权限
 */
const ProtectedRoute: FC<ProtectedRouteProps> = ({
  routerKey,
  children,
  redirectPath = '/auth-pages/404',
}) => {
  const { canAccessRouter, loading, isUserAdmin } = usePermission();
  const { isAuthenticated } = useContext(AuthContext);

  // 权限加载中时，不渲染任何内容（由 Suspense 统一处理 loading）
  // 这样避免出现 2 个 loading spinner
  if (loading) {
    return null;
  }

  // 未登录用户：重定向到登录页（使用绝对路径）
  if (!isAuthenticated) {
    return <Navigate to={`/${authPagesMenu.login.path}`} replace />;
  }

  // 管理员直接放行
  if (isUserAdmin) {
    return children;
  }

  // 如果没有指定 routerKey，直接放行
  if (!routerKey) {
    return children;
  }

  // 检查路由权限（已登录但无权限 → 跳转 404）
  if (!canAccessRouter(routerKey)) {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
